import { Component, OnDestroy, OnInit, ViewEncapsulation, computed, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslocoModule } from '@ngneat/transloco';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User, UserRole } from 'app/core/user/user.types';
import { WorkshopFilterService } from 'app/core/workshop-filter/workshop-filter.service';
import { SelectWorkshopsComponent } from 'app/shared/components/select-workshops/select-workshops.component';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { GlobalSearchComponent } from 'app/layout/common/global-search/global-search.component';
import { filter, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'classy-layout',
  templateUrl: './classy.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    TranslocoModule,
    UserComponent,
    MatIconModule,
    MatButtonModule,
    LanguagesComponent,
    RouterOutlet,
    RouterModule,
    GlobalSearchComponent,
    SelectWorkshopsComponent,
    // FuseFullscreenComponent,
    // SearchComponent,
    // ShortcutsComponent,
    // MessagesComponent,
    // QuickChatComponent
  ]
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
  isScreenSmall: boolean;
  navigationOpened = true;
  navigation: Navigation;
  user: User;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  private readonly _userServiceDI = inject(UserService);
  readonly workshopFilter = inject(WorkshopFilterService);

  /** True when the current user is WORKSHOP — search is hidden for this role */
  readonly isWorkshopUser = computed(
    () => this._userServiceDI.currentUser()?.role === UserRole.WORKSHOP
  );

  readonly showWorkshopSelector = computed(() => {
    const u = this._userServiceDI.currentUser();
    return u?.role === UserRole.WORKSHOP && (u?.workshops?.length ?? 0) > 1;
  });

  /**
   * Constructor
   */
  constructor(
    private _navigationService: NavigationService,
    private _userService: UserService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    private _router: Router
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this._navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation = navigation;
      });

    // Subscribe to the user service
    this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: User) => {
      this.user = user;
      if (!this._isKanbanRoute(this._router.url)) {
        this._syncNavigationOpened();
      }
    });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
        this._syncNavigationOpened();
      });

    // Close navigation when on kanban route
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe((event: NavigationEnd) => {
        if (this._isKanbanRoute(event.urlAfterRedirects)) {
          this.navigationOpened = false;
          this._closeNavigation();
        } else {
          this._syncNavigationOpened();
        }
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  private _syncNavigationOpened(): void {
    this.navigationOpened = !this.isScreenSmall;
  }

  private _closeNavigation(): void {
    setTimeout(() => {
      const navigation =
        this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
          'mainNavigation'
        );
      navigation?.close();
    });
  }

  private _isKanbanRoute(url: string): boolean {
    const path = url.split('?')[0];
    return path === '/kanban' || path.startsWith('/kanban/');
  }

}

